export interface Module {
    id: number,
    name: String,
}

export type CameraCardProps = {
    camera: Camera
    editCamera: () => void
    cameraPreview: () => void
}

export enum CAMERA_TYPES {
    EXTERIOR = "exterior",
    INTERIOR = "interior",
}
export interface Camera {
    id?: number | string,
    cameraId?: number | string,
    systemCode?: string,
    camera_type?: CAMERA_TYPES.EXTERIOR | CAMERA_TYPES.INTERIOR,
    capture_protocol?: string,
    ip_address: string,
    type: CAMERA_TYPES.EXTERIOR | CAMERA_TYPES.INTERIOR,
    camera_position: number,
    username: string,
    password: string,
    brand: string,
    name?: string,
    query_params?: string | null,
    order: number,
    sort_position?: number,
    rooftops?: Configuration[],
    tag: string,
    skip_ricoh_while_charging?: boolean,
    is_under_carriage?: boolean,
    // Barcode/autoscan reader camera. When true, this camera is excluded from
    // normal exterior/interior capture and only used for barcode-triggered scans.
    is_autoscan?: boolean,
    // Hardware serial (Axis serial == MAC). Entered manually in the UMS for
    // autoscan cameras, then synced by bw-unit-local to the exterior camera it
    // applies to.
    serial?: string,
    http_config?: {
        auth_type?: string;
        has_credentials?: boolean;
        ip_address?: string;
        username?: string;
        password?: string;
        query_params?: string | null;
    };
    desired_settings?: {
        skip_ricoh_while_charging?: boolean;
    };
    mqtt_config?: Record<string, unknown> | null;
}

export enum CameraTags {
    Unassigned = "Unassigned",
    LeftFront = "Left Front",
    LeftSide = "Left Side",
    LeftRear = "Left Rear",
    Rear = "Rear",
    RightRear = "Right Rear",
    RightSide = "Right Side",
    RightFront = "Right Front",
    Front = "Front",
    Roof = "Roof",
    LeftFrontLow = "Left Front Low",
    LeftSideLow = "Left Side Low",
    LeftRearLow = "Left Rear Low",
    RearLow = "Rear Low",
    RightRearLow = "Right Rear Low",
    RightSideLow = "Right Side Low",
    RightFrontLow = "Right Front Low",
    FrontLow = "Front Low",
    LeftFrontHigh = "Left Front High",
    LeftSideHigh = "Left Side High",
    LeftRearHigh = "Left Rear High",
    RearHigh = "Rear High",
    RightRearHigh = "Right Rear High",
    RightSideHigh = "Right Side High",
    RightFrontHigh = "Right Front High",
    FrontHigh = "Front High"
}

export interface System {
    systemCode: string,
    commonName: string,
}

export type UnitType = "odroid" | "mobile";

export interface ApiSystem {
  systemCode: string;
  unitIndex: string;
  activeUnitIndex: string;
  // Hardware type of the unit backing this system. Absent/undefined is treated
  // as "odroid" everywhere so pre-existing systems keep their current behaviour.
  unit_type?: UnitType;
  cloudFrontBaseUrl: string;
  commonName: string;
  customerDealIdentifier: string;
  dailyAssetReportEmailList: string;
  IANA_timezone: string;
  system_category?: string;
  configurations: any;
  ums_env: string;
  iframeUcViewCount?: number;
  iframePanoViewCount?: number;
  iframeLastViewedAt?: string;
}

export interface MonitoringBreakdownItem {
  input_method?: string | null;
  rooftop_id?: string | number | null;
  image_type?: string | null;
  count: number;
}

export interface MonitoringBacklog {
  pending_count: number;
  oldest_pending_age_minutes: number | null;
  sample_vins: string[];
}

export interface MonitoringLatestVehicle {
  id: string;
  vin: string;
  created_at_utc: string;
  age_minutes: number;
  ready: boolean;
  ready_for_ums_upload: boolean;
  pushed_cms: boolean;
  pushed_ums: boolean;
  error: boolean;
  retries: number;
  input_method: string | null;
  rooftop_id: string | number | null;
  image_counts: {
    exterior: number;
    interior: number;
    manual: number;
    undercarriage: number;
  };
}

export interface MonitoringSnapshotSummary {
  hostname: string;
  platform: string;
  vehicles_total: number;
  ready_count: number;
  not_ready_count: number;
  throughput_per_hour: number;
  error_count: number;
  recent_error_count: number;
  outage_failure_count: number;
  retrying_count: number;
  in_ums_transit_count: number;
  cms_pending_count: number;
  cms_oldest_pending_age_minutes: number | null;
  ums_pending_count: number;
  ums_oldest_pending_age_minutes: number | null;
  latest_vehicle_age_minutes: number | null;
  files_total: number;
  storage_used_percent: number | null;
  alerts_count: number;
  has_errors?: boolean;
  has_recent_errors?: boolean;
  has_outage_failures?: boolean;
  has_delivery_backlog?: boolean;
  is_retrying?: boolean;
  stale_data?: boolean;
  storage_has_pressure?: boolean;
}

export interface MonitoringCameraLog {
  ts: string;
  host: string;
  ip: string;
  position: string;
  logger: string;
  level: string;
  msg: string;
  replayed?: boolean;
  received_at_utc?: string;
}

export interface MonitoringSnapshot {
  generated_at_utc: string;
  hostname?: string;
  summary?: MonitoringSnapshotSummary;
  window: {
    hours: number;
    start_utc: string;
  };
  host: {
    hostname: string;
    platform: string;
    uptime_seconds: number | null;
    storage: {
      target_dir: string;
      total_bytes?: number;
      used_bytes?: number;
      free_bytes?: number;
      used_percent?: number | null;
      error?: string;
    };
  };
  vehicles: {
    total: number;
    recent_window_count: number;
    throughput_per_hour: number;
    ready_count: number;
    not_ready_count: number;
    error_count: number;
    recent_error_count: number;
    outage_failure_count: number;
    retrying_count: number;
    in_ums_transit_count: number;
    cms_upload_backlog: MonitoringBacklog;
    ums_upload_backlog: MonitoringBacklog;
    input_method_breakdown: MonitoringBreakdownItem[];
    rooftop_breakdown: MonitoringBreakdownItem[];
    latest_vehicle: MonitoringLatestVehicle | null;
  };
  files: {
    total: number;
    image_type_breakdown: MonitoringBreakdownItem[];
  };
  camera_logs?: MonitoringCameraLog[];
  alerts: string[];
}

interface SystemSettings {
  systemCode: string;
  unitIndex: string;
  cloudFrontBaseUrl: string;
  commonName: string;
  customerDealIdentifier: string;
  dailyAssetReportEmailList: string;
  IANA_timezone: string;
}


export interface ConfigurationOverlay {
  id: number,
  order: number,
  manditory: boolean,
  phone_camera_lens: string,
}

export interface Configuration {
    id: number,
    name: string,
    url: string,
    vin_length: number,
    location_id: number,
    s3_bucket: string,
    auto_scan_countdown_length: number,
    manual_scan_countdown_length: number,
    blackout_screen_during_capture: boolean,
    display_screen_sleep_timer: number,
    auto_scan_pano: boolean,
    dual_hand_scan_capture: boolean,
    vin_scan_terminology: string,
    auto_scan_enabled: boolean,
    use_lighting_controller: boolean,
    actuator_leg_stagger_time: number,
    actuator_trigger_count_threshold: number,
    actuator_leg_cycle_time: number,
    actuator_fold_trigger_distance: number,
    vin_feedback_terminology: string,
    use_nhtsa_database: boolean,
    is_default: boolean,
    preferances: {
        pano: boolean,
        manual: boolean,
        exterior: boolean,
        dev: boolean,
        barcode: boolean,
        vinGenerator: boolean,
        multiPano: boolean,
        skipExternal: boolean,
        skipPano: boolean
        vinchar: boolean,
        vinBypass: boolean,
    },
    enabled: Module[],
    disabled: Module[],
    is_display_tablet_rooftop: boolean,
    phone_camera_lens: string,
    phone_camera_resolution: string,
    phone_sync: boolean,
    phone_sync_terminology: string,
    overlays: ConfigurationOverlay[],
    cameras: number[]
    skip_interior_simultaneous: boolean,
}

export interface Asset {
  id: string,
  systemCode: string,
  image_dir: string,
  config: string,
  pushed: boolean,
  ready: boolean,
  vin?: string,
  created_at: string,
  exterior: number,
  interior: number,
  manual: number,
  thumbnail: string,
  status: any,
  imageExterior: any,
  imageManual: any,
  imagePano: any,
  imageFeatured: any,
  imageMask: any,
  imageCrop: any,
  imageExteriorBrand?: any,
  imageFeaturedBrand?: any,
  imageUndercarriage: any,
  imageUndercarriageDewarp: any,
  imageBgr: any, 
  imageBgrExterior?: any,
  imageBgrManual?: any,
  imageBgrPano: any, 
  imageBgBlur?: any,
  imageBgblur?: any,
  imageBgrBlur?: any,
  imageBlur?: any,
  assetId: string,
  assetInstanceId: string,
  assetIdentifier?: string,
  clientIdentifier?: string,
  missingCameraPositions?: string[],
  assetProcessingCompleted?: boolean,
  assetSyndicationCompleted?: boolean,
  pk: string,
  sk: string,
  cloudFrontUrl: string,
  s3bucket: string,
  archived?: boolean,
  processingCurrentStep?: string,
  processingStatus?:  "COMPLETE" | "PENDING" | "ERROR" | "NOT STARTED",
  processingError?: string,
  processingProcessFlagValue?: number | null;
  br_status?: string,
  br_job_id?: string,
  syndicationCurrentStep?: string,
  syndicationStatus?: "COMPLETE" | "PENDING" | "ERROR" | "NOT STARTED",
  syndicationError?: string,
  syndicationSteps?: string[],
  syndicationProcessFlagValue?: number | null;
  cameraDirection?: "cw" | "ccw",
  // Backend-resolved iframe display order, from the asset configuration's
  // clientProcess.iframeImageOrdering (ImageOrder + GroupFallback). Present only
  // on iframe requests; null when the configuration has no iframe ordering.
  iframeImages?: any[] | null,
}

export interface Account {
    createdDate: string,
    email: string,
    first_name: string,
    last_name: string,
    phone_number: string,
    role: string,
    system_access: string [],
    username: string,
    status?: string,
    lastLoginDate?: string
}

export interface User {
  createdDate: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  role: 'Admin' | 'Support' | 'Billing' | 'Auditor' | 'Client' | 'Client Plus' | 'Syndication Partner';
  system_access: any[];
  username: string;
}


export interface OverlayDTO {
    id: number,
    name: string,
    img: string,
    order: number,
    manditory: boolean,
    body_type: string
    sub_type?: string,
    phone_camera_lens: string,
}

export interface ConfigOverlay {
    id: number,
    order: number,
    manditory: boolean,
    phone_camera_lens: string,
}

export interface ClientProcess {
  id?: number;
  name?: string;
  is_default?: boolean;
  cameras?: number[];
  disabled?: Record<string, { id: number; name: string }>;
  enabled?: Record<string, { id: number; name: string }>;
  overlays?: {
    id: number;
    manditory: boolean;
    order: number;
    phone_camera_lens: string;
  }[];
  overlay?: any;
  phone_camera_lens?: string;
  phone_camera_resolution?: string;
  phone_leg_control?: boolean;
  phone_sync?: boolean;
  phone_sync_terminology?: string;
  phone_uc_upload_delay?: boolean;
  manual_scan_countdown_length?: number;
  auto_scan_countdown_length?: number;
  blackout_screen_during_capture?: boolean;
  auto_scan_enabled?: boolean;
  auto_scan_pano?: boolean;
  skip_interior_simultaneous?: boolean;
  tablet_rooftop_select?: boolean;
  is_display_tablet_rooftop?: boolean;
  allow_cancel_vehicle?: boolean;
  display_screen_sleep_timer?: number;
  prefix_removal?: number;
  vin_length?: number;
  vin_scan_terminology?: string;
  vin_feedback_terminology?: string;
  vin_length_err_terminology?: string;
  use_lighting_controller?: boolean;
  use_nhtsa_database?: boolean;
  dual_hand_scan_capture?: boolean;
  ums_environment?: string;
  url?: string;
  s3_bucket?: string;
  preferances?: {
    skipExternal?: boolean;
    skipPano?: boolean;
    vinBypass?: boolean;
    vinchar?: boolean;
  };
  actuator_fold_trigger_distance?: number;
  actuator_leg_cycle_time?: number;
  actuator_leg_stagger_time?: number;
  actuator_trigger_count_threshold?: number;
  location_id?: number;
  phone_camera_lens_alt?: string;
}

export interface AssetProcessingSection {
  [key: string]: Record<string, any>;
}

export interface AssetSyndicationSection {
  [key: string]: Record<string, any>;
}

export interface AssetStorageSection {
  [key: string]: Record<string, any>;
}

export interface NewConfiguration {
  name: string;
  clientProcess?: ClientProcess;
  assetProcessing?: AssetProcessingSection;
  assetStorage?: AssetStorageSection;
  assetSyndication?: AssetSyndicationSection;
}

export interface ReleaseNote {
  noteId: string;
  version: string;
  releaseDate: string;
  bullets: string[];
  createdAt?: string;
}

export interface ReleaseNoteInput {
  version: string;
  releaseDate: string;
  bullets: string[];
}
